<template>
  <div class="modal-card" style="width: 640px">
    <header class="modal-card-head">
      <p class="modal-card-title">{{ title }}</p>
    </header>

    <section class="modal-card-body">
      <!-- Error -->
      <b-message v-if="error" type="is-danger" class="is-size-7">{{ error }}</b-message>

      <!-- Step: Select Carrier -->
      <div v-if="step === 'select'">
        <b-field label="Select Carrier">
          <b-select v-model="carrier" size="is-medium" placeholder="Select a carrier..." expanded :loading="loadingCarriers">
            <option v-for="carrierOption in carriers" :key="carrierOption.id" :value="carrierOption">{{ carrierOption.name }}</option>
          </b-select>
        </b-field>
        <b-message v-if="carrier && carrier.code !== 'UPSN' && carrier.code !== 'FDEG'" type="is-warning" class="is-size-7">
          Only UPS and FedEx carrier accounts can be created at this time.
        </b-message>
        <b-message v-if="carrier && carrier.code === 'FDEG'" type="is-warning" class="is-size-7">
          <strong>Important:</strong> The FedEx setup requires multiple validation steps and must be completed in one session. You will not be able to resume if you close this window.
        </b-message>
      </div>

      <!-- Step: UPS Account Info -->
      <div v-else-if="step === 'ups_account'">
        <UpsCarrierForm v-model="upsForm" />
      </div>

      <!-- Step: UPS Done / OAuth Banner -->
      <div v-else-if="step === 'ups_done'">
        <b-message type="is-success">
          Your UPS carrier account has been created successfully.
        </b-message>
        <b-message type="is-info">
          <p class="has-text-weight-bold mb-xs">Authorization Required</p>
          <p class="is-size-7 mb-sm">Click the link below to authorize your UPS account via the EasyPost partner portal. You can verify the authorization status from the Shipping Accounts list.</p>
          <a :href="partnerOauthUrl" target="_blank" rel="noopener noreferrer" class="has-text-weight-bold">
            Authorize UPS Account &rarr;
          </a>
        </b-message>
      </div>

      <!-- Step: FedEx Account Info -->
      <div v-else-if="step === 'fedex_account'">
        <FedExCarrierForm v-model="fedexForm" />
      </div>

      <!-- Step: FedEx Address Validation -->
      <div v-else-if="step === 'fedex_address'">
        <FedExAddressForm v-model="fedexAddressForm" :prefill="fedexForm ? fedexForm.shipping_address : null" />
      </div>

      <!-- Step: FedEx Invoice Validation -->
      <div v-else-if="step === 'fedex_invoice'">
        <FedExInvoiceForm v-model="fedexInvoiceForm" />
      </div>

      <!-- Step: FedEx Done -->
      <div v-else-if="step === 'fedex_done'">
        <b-message type="is-success">
          Your FedEx carrier account has been created and verified successfully.
        </b-message>
      </div>
    </section>

    <footer class="modal-card-foot space-between">
      <b-button @click="cancel">
        {{ step === 'ups_done' || step === 'fedex_done' ? 'Close' : 'Cancel' }}
      </b-button>

      <b-button
        v-if="step === 'select'"
        type="is-primary"
        :disabled="!carrierIsSupported"
        @click="advanceFromSelect">
        Continue
      </b-button>

      <b-button
        v-else-if="step === 'ups_account'"
        type="is-primary"
        :loading="saving"
        :disabled="saving || !upsForm"
        @click="createUpsAccount">
        Create Account
      </b-button>

      <b-button
        v-else-if="step === 'fedex_account'"
        type="is-primary"
        :loading="saving"
        :disabled="saving || !fedexForm"
        @click="createFedExAccount">
        Next: Validate Address
      </b-button>

      <b-button
        v-else-if="step === 'fedex_address'"
        type="is-primary"
        :loading="saving"
        :disabled="saving || !fedexAddressForm"
        @click="validateFedExAddress">
        Next: Validate Invoice
      </b-button>

      <b-button
        v-else-if="step === 'fedex_invoice'"
        type="is-primary"
        :loading="saving"
        :disabled="saving || !fedexInvoiceForm"
        @click="validateFedExInvoice">
        Finish Setup
      </b-button>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import UpsCarrierForm from './UpsCarrierForm'
import FedExCarrierForm from './FedExCarrierForm'
import FedExAddressForm from './FedExAddressForm'
import FedExInvoiceForm from './FedExInvoiceForm'

export default {
  components: { UpsCarrierForm, FedExCarrierForm, FedExAddressForm, FedExInvoiceForm },

  computed: {
    ...mapGetters(['api', 'context']),
    title () {
      const titles = {
        select: 'Add Carrier Account',
        ups_account: 'UPS — Account Details',
        ups_done: 'UPS — Account Created',
        fedex_account: 'FedEx — Account Details',
        fedex_address: 'FedEx — Address Validation',
        fedex_invoice: 'FedEx — Invoice Validation',
        fedex_done: 'FedEx — Account Created'
      }
      return titles[this.step] || 'Add Carrier Account'
    },
    carrierIsSupported () {
      return this.carrier && (this.carrier.code === 'UPSN' || this.carrier.code === 'FDEG')
    },
    baseEndpoint () {
      return this.context.route + '/shippingaccounts/'
    }
  },
  data () {
    return {
      step: 'select',
      saving: false,
      error: null,
      loadingCarriers: true,
      carriers: [],
      carrier: null,
      upsForm: null,
      partnerOauthUrl: null,
      fedexForm: null,
      fedexAddressForm: null,
      fedexInvoiceForm: null,
      carrierIdentifier: null,
      productionApiKey: null
    }
  },
  methods: {
    cancel () {
      this.$parent.close()
    },
    advanceFromSelect () {
      this.error = null
      this.step = this.carrier.code === 'UPSN' ? 'ups_account' : 'fedex_account'
    },
    async createUpsAccount () {
      this.saving = true
      this.error = null
      try {
        const upsPayload = {
          account_number: this.upsForm.account_number,
          description: this.upsForm.description
        }
        if (this.upsForm.reference) upsPayload.reference = this.upsForm.reference
        const upsRes = await this.$http.post(this.baseEndpoint + 'ups-carrier-account/', upsPayload)
        this.carrierIdentifier = upsRes.data.id
        this.partnerOauthUrl = upsRes.data.credentials.partner_oauth_url
        const childRes = await this.$http.post(this.baseEndpoint + 'easypost-child-account/')
        this.productionApiKey = childRes.data.easypost_production_api_key

        await this.$http.post(this.baseEndpoint, {
          nickname: this.upsForm.nickname,
          carrier_id: this.carrier.id,
          is_first_party_bill: true,
          identifier: this.carrierIdentifier,
          api_token: this.productionApiKey
        })

        this.step = 'ups_done'
        this.$emit('created')
      } catch (e) {
        this.error = 'Something went wrong. Please check your details and try again.'
      } finally {
        this.saving = false
      }
    },

    async createFedExAccount () {
      this.saving = true
      this.error = null
      try {
        const res = await this.$http.post(this.baseEndpoint + 'fedex-carrier-account/', {
          account_number: this.fedexForm.account_number,
          description: this.fedexForm.description,
          shipping_address: this.fedexForm.shipping_address,
          corporate_contact: this.fedexForm.corporate_contact
        })
        this.carrierIdentifier = res.data.id
        this.step = 'fedex_address'
      } catch (e) {
        this.error = 'Failed to create FedEx carrier account. Please check your details and try again.'
      } finally {
        this.saving = false
      }
    },

    async validateFedExAddress () {
      this.saving = true
      this.error = null
      try {
        await this.$http.post(this.baseEndpoint + 'fedex-address-validation/', {
          account_number: this.fedexForm.account_number,
          carrier_account_id: this.carrierIdentifier,
          shipping_address: this.fedexAddressForm
        })
        this.step = 'fedex_invoice'
      } catch (e) {
        this.error = 'Address validation failed. Please verify your address details.'
      } finally {
        this.saving = false
      }
    },

    async validateFedExInvoice () {
      this.saving = true
      this.error = null
      try {
        await this.$http.post(this.baseEndpoint + 'fedex-invoice-validation/', {
          account_number: this.fedexForm.account_number,
          carrier_account_id: this.carrierIdentifier,
          invoice_validation: this.fedexInvoiceForm
        })

        const childRes = await this.$http.post(this.baseEndpoint + 'easypost-child-account/')
        this.productionApiKey = childRes.data.easypost_production_api_key

        await this.$http.post(this.baseEndpoint, {
          nickname: this.fedexForm.nickname,
          carrier_id: this.carrier.id,
          is_first_party_bill: true,
          identifier: this.carrierIdentifier,
          api_token: this.productionApiKey
        })

        this.step = 'fedex_done'
        this.$emit('created')
      } catch (e) {
        this.error = 'Invoice validation failed. Please verify your invoice details.'
      } finally {
        this.saving = false
      }
    },

    loadCarriers () {
      this.loadingCarriers = true
      this.$http.get(this.api.baseUrl + '/v1/carriers/?is_common=1').then(response => {
        this.carriers = response.data.results
      }).finally(() => {
        this.loadingCarriers = false
      })
    }
  },

  mounted () {
    this.loadCarriers()
  }
}
</script>
